// src/components/QlikCard/index.jsx
import { useContext, useState, useCallback } from "react";
import Card from "components/Card";
import QlikObject from "components/QlikObject";
import ExportDataModal from "components/ExportDataModal";
import ExportDataSelectorModal from "components/ExportDataSelectorModal";
import { ExportData, ExportImg, ExportPdf } from "qlik-api/qlikUtils";
import GlobalContext from "GlobalContext";
import {
  dispatchTouchEvent,
  dispatchPointerEvent
} from "utils/utils";
import allowedObjectTypes from "utils/allowedObjectTypes";

export default function QlikCard({
  id,
  title,
  titleIcon,
  height = "300px",
  showMaximize = true,
  showOverlayer = true,
  noSelections = false,
  noInteraction = false,
  showExport = true,
  showImageExport = false,
  showPdfExport = false,
  exportTitle = "",
  exportImageTitle = "",
  exportImageWidth = 1920,
  exportImageHeight = 1080,
  exportImageFormat = "png",
  exportPdfTitle = "",
  exportPdfDocSize = "a4",
  exportPdfOrientation = "landscape",
  exportText = "Exporting data...",
  exportCompletedText = "Export completed. Click here to download the data file.",
  exportSelectObjectText = "Select chart to export",
  exportNoObjectText = "No available objects to export.",
  closeBtnText = "Close",
  showToggleDataView = false,
  showHeader = true,
  onBeforeExportHandler = null,
  onAfterExportHandler = null,
  addClasses = "",
  actionsVisibility = "hover",
  copyCells = false,
  copiedSuccessMessage,
  copiedFailedMessage,
}) {
  const { app, onTouchDevice } = useContext(GlobalContext);
  const [showDataView, setShowDataView] = useState(false);
  const [showExportDataModal, setShowExportDataModal] = useState(false);
  const [exportLink, setExportLink] = useState(null);
  const [exportError, setExportError] = useState("");
  const [allowSelections, setAllowSelections] = useState(!onTouchDevice);
  const [selectorOptions, setSelectorOptions] = useState(null);
  const actions = [];

  // On touch devices only: toggle selections mode
  const onTapChartHandler = useCallback(
    (e) => {
      setAllowSelections(!allowSelections);
      dispatchTouchEvent(e, "touchstart");
      dispatchPointerEvent(e, "pointerdown");
      setTimeout(() => dispatchPointerEvent(e, "pointerup"), 150);
    },
    [allowSelections]
  );

  const onExportStartedHandler = useCallback(async (data) => {
    setExportLink(null);
    setExportError(null);
    setShowExportDataModal(true);
    if (onBeforeExportHandler) await onBeforeExportHandler(data);
  }, [onBeforeExportHandler]);

  const onExportFinishedHandler = useCallback(async (data) => {
    const { link, error } = data;
    setShowExportDataModal(true);
    setExportLink(link);
    setExportError(error);
    if (onAfterExportHandler) await onAfterExportHandler(data);
  }, [onAfterExportHandler]);

  // Handle export click, including layout-container selector
  const handleExportClick = async () => {
    const vis = await app.visualization.get(id);
    const layout = vis.model.layout;
    const qType = layout.qInfo.qType;

    if (qType === "sn-layout-container") {
      const condMap = (layout.objects || []).reduce((m, obj) => {
        m[obj.childRefId] = obj.condition;
        return m;
      }, {});

      const opts = (layout.qChildList.qItems || [])
        .filter(item => Number(condMap[item.qData.childRefId]) !== 0)
        .map(item => ({
          title: item.qData.title,
          qId: item.qInfo.qId,
          vizType: item.qData.visualization
        }))
        .filter(opt => allowedObjectTypes.includes(opt.vizType));

      if (opts.length === 1) {
        // exactly one available: export it immediately
        const singleId = opts[0].qId;
        await ExportData({
          app,
          id: singleId,
          onExportStart: onExportStartedHandler,
          onExportFinish: onExportFinishedHandler
        });
      } else if (opts.length > 1) {
        // multiple: show selector modal
        setSelectorOptions(opts);
      } else {
        // no available objects: show selector with warning
        setSelectorOptions([]);
      }
    } else {
      // not a layout-container → direct export
      await ExportData({
        app,
        id,
        onExportStart: onExportStartedHandler,
        onExportFinish: onExportFinishedHandler
      });
    }
  };

  // After selector pick
  const handleSelectorPick = async qId => {
    setSelectorOptions(null);
    if (qId) {
      await ExportData({
        app,
        id: qId,
        onExportStart: onExportStartedHandler,
        onExportFinish: onExportFinishedHandler
      });
    }
  };

  // Build action buttons
  if (onTouchDevice) {
    actions.push({
      icon: "lui-icon lui-icon--touch",
      btnClasses: ({ maximized }) =>
        allowSelections || maximized ? "active bg-light" : "",
      action: () => setAllowSelections(!allowSelections)
    });
  }

  if (showToggleDataView) {
    actions.push({
      icon: "lui-icon lui-icon--table",
      btnClasses: showDataView ? "active bg-light" : "",
      action: () => setShowDataView(!showDataView)
    });
  }

  if (showExport) {
    actions.push({
      title: exportTitle,
      icon: "xlsx",
      action: handleExportClick
    });
  }

  if (showImageExport) {
    actions.push({
      title: exportImageTitle,
      icon: "png",
      action: () =>
        ExportImg({
          app,
          id,
          width: exportImageWidth,
          height: exportImageHeight,
          format: exportImageFormat,
          onExportStart: onExportStartedHandler,
          onExportFinish: onExportFinishedHandler
        })
    });
  }

  if (showPdfExport) {
    actions.push({
      title: exportPdfTitle,
      icon: "pdf",
      action: () =>
        ExportPdf({
          app,
          id,
          documentSize: exportPdfDocSize,
          orientation: exportPdfOrientation,
          onExportStart: onExportStartedHandler,
          onExportFinish: onExportFinishedHandler
        })
    });
  }

  return (
    <>
      {/* Selector modal for layout-container */}
      {selectorOptions && (
        <ExportDataSelectorModal
          options={selectorOptions}
          onSelect={handleSelectorPick}
          onClose={() => setSelectorOptions(null)}
          exportSelectObjectText={exportSelectObjectText}
          exportNoObjectText={exportNoObjectText}
          closeBtnText={closeBtnText}
        />
      )}

      {/* Export status modal */}
      {showExportDataModal && (
        <ExportDataModal
          title={exportTitle}
          text={exportText}
          exportCompletedText={exportCompletedText}
          exportError={exportError}
          link={exportLink}
          closeBtnText={closeBtnText}
          onCloseHandler={() => setShowExportDataModal(false)}
        />
      )}

      <Card
        title={title}
        titleIcon={titleIcon}
        showMaximize={showMaximize}
        actions={actions}
        addClasses={addClasses}
        showHeader={showHeader}
        actionsVisibility={actionsVisibility}
        allowSelections={allowSelections}
      >
        {({ maximized }) => (
          <QlikObject
            id={id}
            height={height}
            maximized={maximized}
            showOverlayer={showOverlayer}
            showDataView={showDataView}
            noSelections={noSelections}
            noInteraction={noInteraction}
            disableInteractivity={
              showOverlayer && (!allowSelections && !maximized)
            }
            onTapEvent={onTapChartHandler}
            copyCells={copyCells}
            copiedSuccessMessage={copiedSuccessMessage}
            copiedFailedMessage={copiedFailedMessage}
          />
        )}
      </Card>
    </>
  );
}